import api from './api';

/**
 * Imágenes georreferenciadas del cliente, cortadas en mosaicos.
 *
 * El corte NO pasa por aquí: se hace en la computadora de quien sube la
 * imagen, con scripts/cortar-mapa.sh, y se sube un zip ya listo. El servidor
 * sólo lo descomprime y lo sirve. Ver el comentario del módulo en
 * apps/core/src/modules/flights/routes.ts para el porqué.
 */
export interface Vuelo {
  id: string;
  nombre: string;
  /** Fecha de la TOMA (AAAA-MM-DD), no de la subida. */
  fecha: string;
  limites: { minLat: number; minLng: number; maxLat: number; maxLng: number };
  minZoom: number;
  maxZoom: number;
  mosaicos: number;
  bytes: number;
  estado: 'procesando' | 'listo' | 'fallido';
  error: string | null;
  creadoEn: string;
}

export async function getVuelos(): Promise<Vuelo[]> {
  const { data } = await api.get('/flights');
  return data;
}

/**
 * Tamaño de cada trozo.
 *
 * Ocho megas es el punto medio: lo bastante chico para que cada petición
 * dure segundos —y ningún proxy la corte— y lo bastante grande para no
 * convertir una subida de 340 MB en cientos de viajes de ida y vuelta.
 */
const TROZO_BYTES = 8 * 1024 * 1024;

/** Reintentos por trozo antes de darse por vencido. */
const REINTENTOS = 3;

const esperar = (ms: number) => new Promise(r => setTimeout(r, ms));

/**
 * Error de subida que dice EN QUÉ PASO murió.
 *
 * Con la subida por partes hay tres momentos muy distintos —abrir, mandar
 * un trozo, cerrar— y cada uno apunta a una causa diferente. Un "error
 * 502" pelón obliga a ir a los logs del servidor para saber cuál fue; con
 * el paso y el número de trozo, la pantalla ya lo dice.
 */
export class ErrorSubida extends Error {
  constructor(
    readonly paso: 'abrir' | 'trozo' | 'finalizar',
    readonly status: number | null,
    readonly detalle: string | null,
    readonly trozo?: { indice: number; total: number },
  ) {
    super(detalle ?? `Falló al ${paso}`);
    this.name = 'ErrorSubida';
  }
}

const comoErrorSubida = (
  err: any,
  paso: 'abrir' | 'trozo' | 'finalizar',
  trozo?: { indice: number; total: number },
) => new ErrorSubida(paso, err?.response?.status ?? null, err?.response?.data?.error ?? null, trozo);

/**
 * Sube el zip de una imagen, por partes.
 *
 * Antes iba entero en UNA petición que vivía varios minutos cruzando dos
 * proxies. Falló en producción con un 502, y da igual cuál capa cortó: el
 * problema es que una petición larga depende de que TODO el camino aguante
 * todo el rato, y si algo se cae a la mitad se pierden los 340 MB.
 *
 * Por partes, cada petición dura segundos. Un trozo que falla se reintenta
 * solo, sin perder lo ya subido, y la barra de progreso mide algo real.
 */
export async function subirVuelo(archivo: File, onProgreso?: (pct: number) => void): Promise<Vuelo> {
  let sesion: { id: string };
  const total = Math.ceil(archivo.size / TROZO_BYTES);
  try {
    const res = await api.post('/flights/uploads', { bytes: archivo.size });
    sesion = res.data;
  } catch (err) {
    throw comoErrorSubida(err, 'abrir');
  }

  try {
    // El índice avanza SÓLO cuando el servidor confirma el trozo.
    //
    // La primera versión lo incrementaba también al resincronizar tras un
    // 409, así que se saltaba un trozo de 8 MB: el zip llegaba con el
    // tamaño casi correcto y un hueco en medio, y fallaba después con un
    // "no trae metadata.json" que no apuntaba a nada. Por eso el aumento
    // vive junto al éxito y en ningún otro lado.
    let indice = 0;
    let intento = 0;
    while (indice < total) {
      const trozo = archivo.slice(indice * TROZO_BYTES, (indice + 1) * TROZO_BYTES);
      try {
        await api.post(`/flights/uploads/${sesion.id}/parte?indice=${indice}`, trozo, {
          headers: { 'Content-Type': 'application/octet-stream' },
          timeout: 120000,
        });
        indice++;
        intento = 0;
        onProgreso?.(Math.round((indice / total) * 100));
      } catch (err: any) {
        // El servidor pega los trozos en orden; si se le cruzó un reintento
        // tardío, dice cuál espera y se sigue desde ahí — sin avanzar.
        const esperado = err?.response?.data?.esperado;
        if (err?.response?.status === 409 && typeof esperado === 'number') {
          indice = esperado;
          intento = 0;
          continue;
        }
        const status = err?.response?.status;

        // 429 es "vas muy rápido", no "esto está mal": es el único 4xx que
        // se arregla esperando. Se respeta el Retry-After que manda el
        // servidor, y si no viene se espera un par de segundos por intento.
        if (status === 429) {
          const dice = Number(err?.response?.headers?.['retry-after']);
          const espera = Number.isFinite(dice) && dice > 0 ? dice * 1000 : 2000 * (intento + 1);
          if (++intento > REINTENTOS) throw comoErrorSubida(err, 'trozo', { indice: indice + 1, total });
          await esperar(espera);
          continue;
        }

        // Cualquier otro 4xx es un problema real: reintentar no lo arregla y
        // sólo hace esperar al usuario.
        if (status && status >= 400 && status < 500) {
          throw comoErrorSubida(err, 'trozo', { indice: indice + 1, total });
        }
        if (++intento > REINTENTOS) throw comoErrorSubida(err, 'trozo', { indice: indice + 1, total });
        await esperar(600 * intento);
      }
    }

    try {
      const { data } = await api.post(`/flights/uploads/${sesion.id}/finalizar`, {}, { timeout: 300000 });
      return data;
    } catch (err) {
      throw comoErrorSubida(err, 'finalizar');
    }
  } catch (err) {
    // Se avisa al servidor para que suelte el archivo a medias en vez de
    // dejarlo ocupando disco hasta que expire la sesión.
    await api.delete(`/flights/uploads/${sesion.id}`).catch(() => {});
    throw err;
  }
}

export async function borrarVuelo(id: string): Promise<void> {
  await api.delete(`/flights/${id}`);
}

/**
 * Plantilla de URL para Leaflet.
 *
 * Va sin token a propósito: el navegador pide cada mosaico como una imagen
 * normal, y a esas peticiones no se les puede poner un encabezado de
 * autorización. Funciona porque el JWT también vive en una cookie
 * (auth-jwt.ts la configura), así que viaja solo en cada petición y el
 * servidor sigue verificando que el vuelo sea del cliente correcto.
 */
export function plantillaDeMosaicos(id: string): string {
  return `/api/v1/flights/${id}/tiles/{z}/{x}/{y}`;
}

/**
 * Corrige el nombre o la fecha de captura de una imagen.
 *
 * La fecha sale del script de corte, donde es un argumento que se teclea —
 * es fácil que quede la del día de la subida en vez de la de la toma. Sin
 * esto, corregirla obligaba a borrar y volver a subir cientos de megas.
 */
export async function editarVuelo(
  id: string,
  cambios: { nombre?: string; fecha?: string },
): Promise<void> {
  await api.patch(`/flights/${id}`, cambios);
}
